/**
 * Pure logic for the Recipe Builder GUI step builder — deliberately React-
 * free (no import of 'react', no JSX) so it can be exercised by a plain
 * Node self-check without needing `react` resolvable from wherever that
 * check happens to run (CI runs self-checks from the repo root, where a
 * package that only `console/web` depends on is not resolvable — importing
 * the component file itself, which pulls in 'react', broke exactly this
 * way). `RecipeStepBuilder.tsx` re-exports everything here and adds only the
 * component on top.
 *
 * Each step type maps onto a real, already-executed function
 * (`console/shared/registry/library.ts` / `console/runner/interpreter.ts`):
 *   - Click something  -> `click`        (target: testid, else the label as text)
 *   - Type something   -> `fill`         (target: an advanced selector override, else the field as a label)
 *   - Go to a page      -> `goToPage`     (application is inherited from the recipe, never asked per-step)
 *   - I can see something -> `assertText` (no target = the whole page body, comparator "contains")
 *   - Wait               -> `wait`        (a real, minimal pause function — added for this GUI)
 *   - Retry              -> sets `timing.retryCount` on the PRECEDING step (a real, executed retry —
 *                            not its own action; a lone "Retry" with nothing before it does nothing)
 *   - Screenshot         -> sets `screenshotAssertion` on the PRECEDING step (same real mechanism a
 *                            recipe author already gets when checking a screenshot box elsewhere)
 */
import type { RecipeParamValue, RecipeStepDetail } from '../api';

export type GuiStepKind = 'click' | 'type' | 'goto' | 'seeText' | 'wait' | 'retry' | 'screenshot';

export interface GuiStepClick { id: string; kind: 'click'; label: string; testId: string }
export interface GuiStepType { id: string; kind: 'type'; field: string; selector: string; value: string }
export interface GuiStepGoto { id: string; kind: 'goto'; path: string }
export interface GuiStepSeeText { id: string; kind: 'seeText'; text: string }
export interface GuiStepWait { id: string; kind: 'wait'; durationMs: number }
export interface GuiStepRetry { id: string; kind: 'retry'; count: number }
export interface GuiStepScreenshot { id: string; kind: 'screenshot' }

export type GuiStep = GuiStepClick | GuiStepType | GuiStepGoto | GuiStepSeeText | GuiStepWait | GuiStepRetry | GuiStepScreenshot;

let nextId = 0;
/** Not `Date.now()`/`crypto.randomUUID()` — this only needs to be unique
 *  within one open modal's lifetime, and a plain counter is trivially
 *  reproducible in tests. */
export function newGuiStepId(): string {
  nextId += 1;
  return `gs-${nextId}`;
}

export function blankGuiStep(kind: GuiStepKind): GuiStep {
  const id = newGuiStepId();
  switch (kind) {
    case 'click': return { id, kind, label: '', testId: '' };
    case 'type': return { id, kind, field: '', selector: '', value: '' };
    case 'goto': return { id, kind, path: '' };
    case 'seeText': return { id, kind, text: '' };
    case 'wait': return { id, kind, durationMs: 5000 };
    case 'retry': return { id, kind, count: 1 };
    case 'screenshot': return { id, kind };
  }
}

export const STEP_TYPE_LABELS: Record<GuiStepKind, string> = {
  click: 'Click something',
  type: 'Type something',
  goto: 'Go to a page',
  seeText: 'I can see something',
  wait: 'Wait',
  retry: 'Retry',
  screenshot: 'Screenshot',
};

// ─────────────────────────────────────────────────────────────────────────────
// Compile: GUI steps -> real RecipeStep[] (+ which compiled step each row landed on)
// ─────────────────────────────────────────────────────────────────────────────

function literal(value: string | number | boolean): RecipeParamValue {
  return { kind: 'literal', value };
}

export interface CompiledGuiSteps {
  steps: RecipeStepDetail[];
  /** Which compiled `steps[]` index a given GUI row ended up on — `null` for
   *  a modifier row (Retry/Screenshot) with nothing before it to attach to. */
  rowToStepIndex: Map<string, number | null>;
}

export function compileGuiSteps(application: string, rows: GuiStep[]): CompiledGuiSteps {
  const steps: RecipeStepDetail[] = [];
  const rowToStepIndex = new Map<string, number | null>();

  for (const row of rows) {
    if (row.kind === 'click') {
      const label = row.label.trim();
      const target = row.testId.trim() ? `testid: ${row.testId.trim()}` : `text: ${label}`;
      steps.push({ functionName: 'click', label: label || undefined, params: { target: literal(target) } });
      rowToStepIndex.set(row.id, steps.length - 1);
    } else if (row.kind === 'type') {
      const field = row.field.trim();
      const target = row.selector.trim() || `label: ${field}`;
      steps.push({
        functionName: 'fill',
        label: field ? `Type into ${field}` : undefined,
        params: { target: literal(target), value: literal(row.value) },
      });
      rowToStepIndex.set(row.id, steps.length - 1);
    } else if (row.kind === 'goto') {
      const urlPath = row.path.trim();
      steps.push({
        functionName: 'goToPage',
        label: `Go to ${urlPath}`,
        params: { application: literal(application), path: literal(urlPath) },
      });
      rowToStepIndex.set(row.id, steps.length - 1);
    } else if (row.kind === 'seeText') {
      const text = row.text.trim();
      steps.push({
        functionName: 'assertText',
        label: `I can see "${text}"`,
        params: { comparator: literal('contains'), expected: literal(text) },
      });
      rowToStepIndex.set(row.id, steps.length - 1);
    } else if (row.kind === 'wait') {
      steps.push({
        functionName: 'wait',
        label: `Wait ${row.durationMs}ms`,
        params: { durationMs: literal(row.durationMs) },
      });
      rowToStepIndex.set(row.id, steps.length - 1);
    } else if (row.kind === 'retry') {
      const targetIndex = steps.length - 1;
      // count:1 is the deliberate "leave it, this is a no-op" default — real
      // retryCount is only set (and only real-executed) above 1.
      if (targetIndex >= 0 && row.count > 1) {
        steps[targetIndex] = { ...steps[targetIndex], timing: { retryCount: row.count, retryIntervalMs: 300 } };
        rowToStepIndex.set(row.id, targetIndex);
      } else {
        rowToStepIndex.set(row.id, targetIndex >= 0 ? targetIndex : null);
      }
    } else if (row.kind === 'screenshot') {
      const targetIndex = steps.length - 1;
      if (targetIndex >= 0) {
        const target = steps[targetIndex];
        steps[targetIndex] = {
          ...target,
          screenshotAssertion: { name: `step-${targetIndex + 1}`, description: target.label },
        };
        rowToStepIndex.set(row.id, targetIndex);
      } else {
        rowToStepIndex.set(row.id, null);
      }
    }
  }

  return { steps, rowToStepIndex };
}

// ─────────────────────────────────────────────────────────────────────────────
// A minimal, block-style YAML writer — deliberately NOT generic. It only ever
// emits the finite set of shapes `compileGuiSteps`/`recipeStepsToYaml` produce,
// matching `console/shared/yamlLite.ts`'s reader (which does NOT support flow
// mappings `{...}` — only block mappings and flow sequences of scalars).
// ─────────────────────────────────────────────────────────────────────────────

function yamlScalar(v: string | number | boolean): string {
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function emitParamValue(pv: RecipeParamValue, indent: string): string[] {
  const lines = [`${indent}kind: ${pv.kind}`];
  if (pv.kind === 'literal') lines.push(`${indent}value: ${yamlScalar(pv.value as string | number | boolean)}`);
  else if (pv.kind === 'variable') lines.push(`${indent}name: ${yamlScalar(pv.name)}`);
  else if (pv.kind === 'expression') lines.push(`${indent}source: ${yamlScalar(pv.source)}`);
  else if (pv.kind === 'column') lines.push(`${indent}column: ${yamlScalar(pv.column)}`);
  return lines;
}

function emitStep(step: RecipeStepDetail, indent: string): string[] {
  const lines = [`${indent}- functionName: ${step.functionName}`];
  const inner = `${indent}  `;
  if (step.label) lines.push(`${inner}label: ${yamlScalar(step.label)}`);
  const paramEntries = Object.entries(step.params ?? {});
  if (paramEntries.length) {
    lines.push(`${inner}params:`);
    for (const [key, pv] of paramEntries) {
      lines.push(`${inner}  ${key}:`);
      lines.push(...emitParamValue(pv, `${inner}    `));
    }
  }
  if (step.timing) {
    lines.push(`${inner}timing:`);
    if (step.timing.retryCount !== undefined) lines.push(`${inner}  retryCount: ${step.timing.retryCount}`);
    if (step.timing.retryIntervalMs !== undefined) lines.push(`${inner}  retryIntervalMs: ${step.timing.retryIntervalMs}`);
  }
  if (step.screenshotAssertion) {
    lines.push(`${inner}screenshotAssertion:`);
    lines.push(`${inner}  name: ${yamlScalar(step.screenshotAssertion.name)}`);
    if (step.screenshotAssertion.description) lines.push(`${inner}  description: ${yamlScalar(step.screenshotAssertion.description)}`);
    if (step.screenshotAssertion.target) lines.push(`${inner}  target: ${yamlScalar(step.screenshotAssertion.target)}`);
  }
  return lines;
}

export function recipeStepsToYaml(name: string, application: string, mutating: boolean, steps: RecipeStepDetail[]): string {
  const lines = [
    `name: ${yamlScalar(name)}`,
    `application: ${application}`,
    `mutating: ${mutating}`,
    'steps:',
  ];
  for (const step of steps) lines.push(...emitStep(step, '  '));
  return `${lines.join('\n')}\n`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reverse: an EXISTING recipe's real steps -> GUI rows, best-effort — used by
// "start from an existing recipe". A step this cannot represent as one of the
// seven simple types makes the WHOLE reverse-mapping bail out (returns null)
// rather than silently dropping or mis-showing it; the caller falls back to
// Advanced/raw YAML, pre-filled from the real recipe, instead.
// ─────────────────────────────────────────────────────────────────────────────

function literalString(pv: RecipeParamValue | undefined): string | null {
  return pv && pv.kind === 'literal' && typeof pv.value === 'string' ? pv.value : null;
}

function literalNumber(pv: RecipeParamValue | undefined): number | null {
  if (!pv || pv.kind !== 'literal') return null;
  const n = typeof pv.value === 'number' ? pv.value : Number(pv.value);
  return Number.isFinite(n) ? n : null;
}

export function reverseMapRecipeSteps(steps: RecipeStepDetail[], application: string): GuiStep[] | null {
  const rows: GuiStep[] = [];

  for (const step of steps) {
    const target = literalString(step.params?.target);

    if (step.functionName === 'click' && target !== null) {
      const testIdMatch = /^testid:\s*(.+)$/i.exec(target);
      const textMatch = /^text:\s*(.+)$/i.exec(target);
      if (testIdMatch) rows.push({ id: newGuiStepId(), kind: 'click', label: step.label ?? '', testId: testIdMatch[1].trim() });
      else if (textMatch) rows.push({ id: newGuiStepId(), kind: 'click', label: textMatch[1].trim(), testId: '' });
      else return null;
    } else if (step.functionName === 'fill' && target !== null) {
      const value = literalString(step.params?.value) ?? '';
      const labelMatch = /^label:\s*(.+)$/i.exec(target);
      if (labelMatch) rows.push({ id: newGuiStepId(), kind: 'type', field: labelMatch[1].trim(), selector: '', value });
      else rows.push({ id: newGuiStepId(), kind: 'type', field: '', selector: target, value });
    } else if (step.functionName === 'goToPage') {
      const stepApp = literalString(step.params?.application);
      const path = literalString(step.params?.path);
      if (path === null || (stepApp !== null && stepApp !== application)) return null;
      rows.push({ id: newGuiStepId(), kind: 'goto', path });
    } else if (step.functionName === 'assertText' && target === null) {
      const comparator = literalString(step.params?.comparator);
      const expected = literalString(step.params?.expected);
      if (expected === null || (comparator !== null && comparator !== 'contains')) return null;
      rows.push({ id: newGuiStepId(), kind: 'seeText', text: expected });
    } else if (step.functionName === 'wait') {
      const durationMs = literalNumber(step.params?.durationMs) ?? 5000;
      rows.push({ id: newGuiStepId(), kind: 'wait', durationMs });
    } else if (step.functionName === 'assertScreenshot') {
      // A standalone screenshot step (rather than one folded onto a preceding
      // step's `screenshotAssertion`) — still just "screenshot" in GUI terms.
      rows.push({ id: newGuiStepId(), kind: 'screenshot' });
    } else {
      return null;
    }

    if (step.timing?.retryCount && step.timing.retryCount > 1) {
      rows.push({ id: newGuiStepId(), kind: 'retry', count: step.timing.retryCount });
    }
    if (step.screenshotAssertion) {
      rows.push({ id: newGuiStepId(), kind: 'screenshot' });
    }
  }

  return rows;
}
